import type { Configuration } from '@azure/msal-browser';

interface RuntimeAuthConfig {
  clientId?: string;
  authority?: string;
  tenantId?: string;
  redirectUri?: string;
  postLogoutRedirectUri?: string;
  scopes?: string;
  apiScopes?: string;
  onDemandApiScopes?: string;
  detApiScopes?: string;
  cacheLocation?: string;
}

const runtimeConfig: RuntimeAuthConfig = window.environment?.aad ?? {};

const localEntityApiScope = 'api://entity-portal-api-test/access_as_user';
const localOnDemandApiScope = 'api://on-demand-api-test/access_as_user';
const localDetApiScope = 'api://det-api-test/access_as_user';

const isLocalLikeEnvironment =
  (window.environment?.name ?? '').toLowerCase() === 'local' ||
  (window.environment?.name ?? '').toLowerCase() === 'development' ||
  import.meta.env.MODE === 'development' ||
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

const getFirstDefined = (
  ...values: Array<string | undefined>
): string | undefined => {
  return values
    .find((value) => typeof value === 'string' && value.trim().length > 0)
    ?.trim();
};

const tenantId = getFirstDefined(
  import.meta.env.VITE_AAD_TENANT_ID,
  import.meta.env.VITE_AUTH_TENANT_ID,
  runtimeConfig.tenantId,
);

const authority = getFirstDefined(
  import.meta.env.VITE_AAD_AUTHORITY,
  import.meta.env.VITE_AUTH_AUTHORITY,
  runtimeConfig.authority,
  tenantId ? `https://login.microsoftonline.com/${tenantId}` : undefined,
);

const clientId = getFirstDefined(
  import.meta.env.VITE_AAD_CLIENT_ID,
  import.meta.env.VITE_AUTH_CLIENT_ID,
  runtimeConfig.clientId,
);

const redirectUri = getFirstDefined(
  import.meta.env.VITE_AAD_REDIRECT_URI,
  import.meta.env.VITE_AUTH_REDIRECT_URI,
  runtimeConfig.redirectUri,
  window.location.origin,
);

const defaultPostLogoutRedirectUri = `${window.location.origin}/login`;

const postLogoutRedirectUri = getFirstDefined(
  import.meta.env.VITE_AAD_POST_LOGOUT_REDIRECT_URI,
  import.meta.env.VITE_AUTH_POST_LOGOUT_REDIRECT_URI,
  runtimeConfig.postLogoutRedirectUri,
  defaultPostLogoutRedirectUri,
);

const scopesSource = getFirstDefined(
  import.meta.env.VITE_AAD_SCOPES,
  import.meta.env.VITE_AUTH_SCOPES,
  runtimeConfig.scopes,
  'openid profile email User.Read',
);

const scopes = (scopesSource ?? 'openid profile email User.Read')
  .split(/[\s,]+/)
  .map((scope) => scope.trim())
  .filter(Boolean);

const fallbackApiScopesSource = isLocalLikeEnvironment
  ? localEntityApiScope
  : undefined;

const apiScopesSource = getFirstDefined(
  import.meta.env.VITE_API_SCOPES,
  runtimeConfig.apiScopes,
  fallbackApiScopesSource,
);

const apiScopes = (
  apiScopesSource ??
  scopesSource ??
  'openid profile email User.Read'
)
  .split(/[\s,]+/)
  .map((scope) => scope.trim())
  .filter(Boolean);

const onDemandApiScopesSource = getFirstDefined(
  import.meta.env.VITE_ON_DEMAND_API_SCOPES,
  runtimeConfig.onDemandApiScopes,
  isLocalLikeEnvironment ? localOnDemandApiScope : undefined,
  apiScopesSource,
);

const onDemandApiScopes = (
  onDemandApiScopesSource ??
  apiScopesSource ??
  'openid profile email User.Read'
)
  .split(/[\s,]+/)
  .map((scope) => scope.trim())
  .filter(Boolean);

const detApiScopesSource = getFirstDefined(
  import.meta.env.VITE_DET_API_SCOPES,
  runtimeConfig.detApiScopes,
  isLocalLikeEnvironment ? localDetApiScope : undefined,
);

const detApiScopes = (detApiScopesSource ?? '')
  .split(/[\s,]+/)
  .map((scope) => scope.trim())
  .filter(Boolean);

const cacheLocationSource = getFirstDefined(
  import.meta.env.VITE_AAD_CACHE_LOCATION,
  runtimeConfig.cacheLocation,
  'sessionStorage',
);

const cacheLocation =
  cacheLocationSource === 'localStorage' ? 'localStorage' : 'sessionStorage';

export const authConfig = {
  clientId,
  authority,
  redirectUri,
  postLogoutRedirectUri,
  scopes,
  apiScopes,
  onDemandApiScopes,
  detApiScopes,
  cacheLocation,
  isConfigured: Boolean(clientId && authority),
};

export const msalConfiguration: Configuration = {
  auth: {
    clientId: clientId ?? '',
    authority,
    redirectUri,
    postLogoutRedirectUri,
  },
  cache: {
    cacheLocation,
  },
};
